import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  Container,
  Group,
  MantineTheme,
  Paper,
  Progress,
  SimpleGrid,
  Skeleton,
  Stack,
  SegmentedControl,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../common/PageHeader';
import { SubawardeeFinancialsCard } from './SubawardeeFinancialsCard';
import { TcfMatchSection } from './TcfMatchSection';
import {
  GivebackAtRiskOverviewCard,
  SubawardeesAtRiskIndicator,
  nameGivebackAtRiskSubawardees,
  type GivebackAtRiskSummary,
  summarizeGivebackAtRisk,
} from '../financials/GivebackRiskCard';
import {
  AllProjectsSpendOverview,
  ProgramSpendCard,
  hasProgramBudgets,
  type SpendMetricTone,
} from '../financials/AllProjectsSpendOverview';
import { financialsApi } from '../../api/financials';
import { useAdminComponentProjectFilterStore } from '../../stores/adminComponentProjectFilterStore';
import { useGroup } from '../../contexts/GroupContext';
import { useAuthStore } from '../../stores/authStore';
import { findComponentProjectById } from '../../config/componentProjects';
import {
  buildSubawardeeLabelLookup,
  getSubawardeeDisplayLabel,
} from '../../utils/adminSubawardeeUtils';
import { ComponentProjectFinancials } from '../../types/financials';

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

const formatCurrency = (n: number): string => {
  if (!isFiniteNumber(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

function pctOfTarget(actual: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((actual / target) * 100);
}

function projectCardPaperSx(theme: MantineTheme) {
  return {
    border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
    boxShadow:
      theme.colorScheme === 'dark'
        ? '0 0 12px rgba(255, 255, 255, 0.1)'
        : theme.shadows.md,
    '@media (max-width: 768px)': {
      margin: theme.spacing.sm,
    },
  };
}

function projectCardToggleButtonSx(theme: MantineTheme) {
  return {
    width: '100%',
    display: 'block',
    textAlign: 'left' as const,
    color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.colors.dark[9],
    borderRadius: theme.radius.sm,
    padding: theme.spacing.xs,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },
    '&:focus-visible': {
      outline: `2px solid ${
        theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
      }`,
      outlineOffset: 2,
    },
  };
}

const SPEND_METRIC_COLORS: Record<SpendMetricTone, string> = {
  federal: 'blue',
  tcf: 'violet',
  match: 'cyan',
  earned: 'teal',
};

function progressColor(
  tone: SpendMetricTone,
  overTarget: boolean,
  pct: number | null
): string {
  if (overTarget) return 'orange';
  if (pct !== null && pct >= 100) return 'green';
  return SPEND_METRIC_COLORS[tone];
}

/** Compact bar used in accordion headers when space is tight. */
function ProjectRollupMetric({
  label,
  value,
  target,
  tone = 'federal',
}: {
  label: string;
  value: number | null;
  target: number | null;
  tone?: SpendMetricTone;
}) {
  const hasValue = isFiniteNumber(value);
  const hasTarget = isFiniteNumber(target);
  const pct = hasValue && hasTarget ? pctOfTarget(value, target) : null;
  const barPct = pct === null ? 0 : Math.min(pct, 100);
  const overTarget = hasValue && hasTarget && value > target;

  return (
    <Stack spacing={6}>
      <Group position="apart" noWrap>
        <Text size="sm" weight={600}>
          {label}
        </Text>
        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {hasValue ? formatCurrency(value) : '—'}
          <Text span size="xs" color="dimmed" weight={500}>
            {' '}
            / {hasTarget ? formatCurrency(target) : '—'}
          </Text>
        </Text>
      </Group>
      <Progress
        value={hasValue ? barPct : 0}
        size="md"
        radius="xl"
        color={progressColor(tone, overTarget, pct)}
      />
    </Stack>
  );
}

function sortFinancialItems(
  financials: ComponentProjectFinancials,
  labelById: Record<string, string>
) {
  return [...financials.items].sort((a, b) => {
    const labelA = getSubawardeeDisplayLabel(a.subawardeeId, labelById) ?? a.subawardeeId;
    const labelB = getSubawardeeDisplayLabel(b.subawardeeId, labelById) ?? b.subawardeeId;
    return labelA.localeCompare(labelB);
  });
}

function projectTitle(financials: ComponentProjectFinancials): string {
  return (
    findComponentProjectById(financials.componentProjectId)?.name ??
    financials.componentProjectId
  );
}

/** Compact bars for accordion always-visible strip. */
function ProjectSpendBars({ financials }: { financials: ComponentProjectFinancials }) {
  const useProgram = hasProgramBudgets(financials);

  return (
    <SimpleGrid
      cols={useProgram ? 4 : 3}
      spacing="md"
      breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
    >
      <ProjectRollupMetric
        label="Federal Spend"
        value={financials.federalSpendToDate}
        target={financials.federalBudget ?? financials.federalTarget}
        tone="federal"
      />
      {useProgram ? (
        <>
          <ProjectRollupMetric
            label="TCF Match"
            value={financials.tcfMatchToDate}
            target={financials.tcfMatchBudget}
            tone="tcf"
          />
          <ProjectRollupMetric
            label="Sub-awardee Match"
            value={financials.matchSpendToDate}
            target={financials.subawardeeMatchBudget}
            tone="match"
          />
        </>
      ) : (
        <ProjectRollupMetric
          label="Match Spend"
          value={financials.matchSpendToDate}
          target={financials.matchTarget}
          tone="match"
        />
      )}
      <ProjectRollupMetric
        label="Earned Revenue"
        value={financials.earnedRevenueToDate}
        target={null}
        tone="earned"
      />
    </SimpleGrid>
  );
}

/** Overview cards for a component project (Federal / TCF Match / Subawardee Match / Giveback). */
function ProjectSpendOverviewCards({
  financials,
  givebackAtRisk,
  labelById,
}: {
  financials: ComponentProjectFinancials;
  givebackAtRisk: GivebackAtRiskSummary;
  labelById: Record<string, string>;
}) {
  const useProgram = hasProgramBudgets(financials);
  const federalBudget = financials.federalBudget ?? financials.federalTarget;
  const atRiskSubawardees = nameGivebackAtRiskSubawardees(
    givebackAtRisk,
    labelById,
    getSubawardeeDisplayLabel
  );

  return (
    <Stack spacing="md">
      <SimpleGrid
        cols={useProgram ? 3 : 2}
        spacing="md"
        breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
      >
        <ProgramSpendCard
          label="Federal Spend"
          value={financials.federalSpendToDate}
          target={federalBudget}
          emptyLabel="No federal spend reported yet"
          tone="federal"
        />
        {useProgram ? (
          <>
            <ProgramSpendCard
              label="TCF Match"
              value={financials.tcfMatchToDate}
              target={financials.tcfMatchBudget}
              tone="tcf"
            />
            <ProgramSpendCard
              label="Sub-awardee Match"
              value={financials.matchSpendToDate}
              target={financials.subawardeeMatchBudget}
              emptyLabel="No sub-awardee match spend reported yet"
              tone="match"
            />
          </>
        ) : (
          <ProgramSpendCard
            label="Match Spend"
            value={financials.matchSpendToDate}
            target={financials.matchTarget}
            emptyLabel="No match spend reported yet"
            tone="match"
          />
        )}
      </SimpleGrid>
      <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <ProgramSpendCard
          label="Earned Revenue"
          value={financials.earnedRevenueToDate}
          target={null}
          emptyLabel="No earned revenue reported yet"
          tone="earned"
        />
        <GivebackAtRiskOverviewCard
          subawardeeCount={givebackAtRisk.count}
          dollars={givebackAtRisk.dollars}
          atRiskSubawardees={atRiskSubawardees}
        />
      </SimpleGrid>
    </Stack>
  );
}

function ProjectSubawardeeCards({
  financials,
  labelById,
  onOpenSubawardee,
}: {
  financials: ComponentProjectFinancials;
  labelById: Record<string, string>;
  onOpenSubawardee: (subawardeeId: string) => void;
}) {
  const sortedItems = sortFinancialItems(financials, labelById);

  if (sortedItems.length === 0) {
    return (
      <Alert color="blue">No sub-awardee financials found for this component project.</Alert>
    );
  }

  return (
    <SimpleGrid
      cols={3}
      spacing="lg"
      breakpoints={[
        { maxWidth: 'md', cols: 2 },
        { maxWidth: 'sm', cols: 1 },
        { maxWidth: 480, cols: 1, spacing: 'md' },
      ]}
    >
      {sortedItems.map((item) => (
        <SubawardeeFinancialsCard
          key={`${financials.componentProjectId}:${item.subawardeeId}`}
          item={item}
          title={getSubawardeeDisplayLabel(item.subawardeeId, labelById) ?? item.subawardeeId}
          onOpen={onOpenSubawardee}
        />
      ))}
    </SimpleGrid>
  );
}

function SpendBarSkeleton() {
  return (
    <Stack spacing={6}>
      <Group position="apart" noWrap>
        <Skeleton height={14} width={90} />
        <Skeleton height={14} width={120} />
      </Group>
      <Skeleton height={8} radius="xl" />
    </Stack>
  );
}

function SubawardeeCardSkeleton() {
  return (
    <Paper p="md" shadow="md" radius="md" sx={(theme) => projectCardPaperSx(theme)}>
      <Stack spacing="md">
        <Skeleton height={22} width="55%" />
        <SpendBarSkeleton />
        <SpendBarSkeleton />
        <SpendBarSkeleton />
        <Group position="apart" mt={4}>
          <Skeleton height={24} width={100} radius="xl" />
          <Skeleton height={36} width={100} radius="sm" />
        </Group>
      </Stack>
    </Paper>
  );
}

/** Loading placeholder that mirrors collapsed project cards / single-project overview. */
function AdminFinancialsPageSkeleton({ showAllProjects }: { showAllProjects: boolean }) {
  if (showAllProjects) {
    return (
      <Stack spacing="lg" mb="xl">
        {Array.from({ length: 3 }).map((_, i) => (
          <Paper key={i} p="md" shadow="md" radius="md" sx={(theme) => projectCardPaperSx(theme)}>
            <Stack spacing="sm">
              <Group spacing="sm" noWrap>
                <Skeleton height={18} width={18} circle />
                <Skeleton height={22} width="35%" />
              </Group>
              <Box pl={26}>
                <SimpleGrid
                  cols={4}
                  spacing="md"
                  breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
                >
                  <SpendBarSkeleton />
                  <SpendBarSkeleton />
                  <SpendBarSkeleton />
                  <SpendBarSkeleton />
                </SimpleGrid>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={60} mb="xl">
      <Stack spacing="md">
        <SimpleGrid cols={3} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Box
              key={`overview-top-${i}`}
              p="md"
              sx={(theme) => ({
                borderRadius: theme.radius.md,
                backgroundColor:
                  theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                border: `1px solid ${
                  theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
                }`,
              })}
            >
              <Stack spacing="sm">
                <Skeleton height={16} width="55%" />
                <Skeleton height={28} width="70%" />
                <Skeleton height={12} width="45%" />
                <Skeleton height={8} radius="xl" mt={4} />
                <Group position="apart" noWrap>
                  <Skeleton height={14} width={70} />
                  <Skeleton height={12} width={90} />
                </Group>
              </Stack>
            </Box>
          ))}
        </SimpleGrid>
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Box
              key={`overview-bottom-${i}`}
              p="md"
              sx={(theme) => ({
                borderRadius: theme.radius.md,
                backgroundColor:
                  theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                border: `1px solid ${
                  theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
                }`,
              })}
            >
              <Stack spacing="sm">
                <Skeleton height={16} width="55%" />
                <Skeleton height={28} width="70%" />
                <Skeleton height={12} width="45%" />
                <Skeleton height={8} radius="xl" mt={4} />
                <Group position="apart" noWrap>
                  <Skeleton height={14} width={70} />
                  <Skeleton height={12} width={90} />
                </Group>
              </Stack>
            </Box>
          ))}
        </SimpleGrid>
      </Stack>

      <Stack spacing="md">
        <Skeleton height={36} radius="sm" />
        <SimpleGrid
          cols={3}
          spacing="lg"
          breakpoints={[
            { maxWidth: 'md', cols: 2 },
            { maxWidth: 'sm', cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' },
          ]}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <SubawardeeCardSkeleton key={i} />
          ))}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
}

interface ComponentProjectFinancialsCardProps {
  financials: ComponentProjectFinancials;
  labelById: Record<string, string>;
  onOpenSubawardee: (subawardeeId: string) => void;
}

/** Per-project financials card — same Paper/Collapse pattern as outstanding milestones. */
function ComponentProjectFinancialsCard({
  financials,
  labelById,
  onOpenSubawardee,
}: ComponentProjectFinancialsCardProps) {
  const [expanded, setExpanded] = useState(false);
  const title = projectTitle(financials);
  const panelId = `financials-panel-${financials.componentProjectId}`;
  const atRisk = summarizeGivebackAtRisk(financials.items);

  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={
        atRisk.count > 0 ? `${title} financials, giveback risk` : `${title} financials`
      }
      sx={(theme) => projectCardPaperSx(theme)}
    >
      <Stack spacing="sm">
        {/* Entire summary surface (header + spend bars) toggles the accordion. */}
        <UnstyledButton
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={panelId}
          sx={(theme) => ({
            ...projectCardToggleButtonSx(theme),
            width: '100%',
          })}
        >
          <Stack spacing="sm">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
              }}
            >
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {expanded ? (
                  <IconChevronDown size={18} />
                ) : (
                  <IconChevronRight size={18} />
                )}
              </Box>
              <Text weight={600} size="lg" lineClamp={1} sx={{ flexShrink: 1, minWidth: 0 }}>
                {title}
              </Text>
              <Box sx={{ flexShrink: 0 }}>
                <SubawardeesAtRiskIndicator count={atRisk.count} dollars={atRisk.dollars} />
              </Box>
            </Box>

            <Box pl={26}>
              <ProjectSpendBars financials={financials} />
            </Box>
          </Stack>
        </UnstyledButton>

        <Collapse in={expanded}>
          <Box id={panelId} mt="xs" pl={26}>
            <ProjectSubawardeeCards
              financials={financials}
              labelById={labelById}
              onOpenSubawardee={onOpenSubawardee}
            />
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
}

/**
 * Admin Financials list: summary cards for each subawardee.
 * When no component project is selected, shows all configured projects
 * in contained, collapsible panels (matching outstanding milestones UX).
 */
export function AdminFinancialsPage() {
  const navigate = useNavigate();
  const componentProject = useAdminComponentProjectFilterStore((s) => s.componentProject);
  const { availableGroups } = useGroup();
  const canEditTcf = useAuthStore((s) => s.hasRole('admin'));
  const showAllProjects = !componentProject;
  const [detailView, setDetailView] = useState<'subawardees' | 'tcf-match'>('subawardees');

  const {
    data: projectFinancials,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['component-project-financials', componentProject ?? 'all'],
    queryFn: async (): Promise<ComponentProjectFinancials[]> => {
      if (componentProject) {
        return [await financialsApi.getComponentProjectFinancials(componentProject)];
      }
      return financialsApi.getAllComponentProjectFinancials();
    },
    staleTime: 5 * 60 * 1000,
    // Always refetch on mount so newly mapped fields (e.g. givebackRisk) are not
    // stuck behind an in-memory cache from an earlier session/HMR.
    refetchOnMount: 'always',
  });

  const labelById = useMemo(
    () => buildSubawardeeLabelLookup(availableGroups),
    [availableGroups]
  );

  const selectedProjectFinancials = !showAllProjects ? projectFinancials?.[0] : undefined;
  const selectedProjectAtRisk = useMemo(
    () =>
      selectedProjectFinancials
        ? summarizeGivebackAtRisk(selectedProjectFinancials.items)
        : { count: 0, dollars: 0, subawardees: [] },
    [selectedProjectFinancials]
  );

  const openSubawardee = (subawardeeId: string) => {
    navigate(`/admin/financials/${encodeURIComponent(subawardeeId)}`);
  };

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Financials"
        subtitle={
          showAllProjects
            ? 'Track program budgets and rolled-up sub-awardee spend across all component projects.'
            : 'Track program budgets and rolled-up sub-awardee spend for your component project.'
        }
      />

      {error ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          role="alert"
          mx="sm"
          mb="xl"
        >
          Failed to load financials. Please try again later.
        </Alert>
      ) : null}

      {showAllProjects ? (
        <Box mb={60}>
          <AllProjectsSpendOverview showTitle={false} />
        </Box>
      ) : null}

      {isLoading ? <AdminFinancialsPageSkeleton showAllProjects={showAllProjects} /> : null}

      {projectFinancials && !error ? (
        <Stack spacing={60} mb="xl">
          {!showAllProjects && selectedProjectFinancials ? (
            <>
              <ProjectSpendOverviewCards
                financials={selectedProjectFinancials}
                givebackAtRisk={selectedProjectAtRisk}
                labelById={labelById}
              />

              {hasProgramBudgets(selectedProjectFinancials) ? (
                <Stack spacing="md">
                  <SegmentedControl
                    value={detailView}
                    onChange={(value) =>
                      setDetailView(value as 'subawardees' | 'tcf-match')
                    }
                    data={[
                      { label: 'Sub-awardees', value: 'subawardees' },
                      { label: 'TCF Match', value: 'tcf-match' },
                    ]}
                    fullWidth
                    sx={(theme) => ({
                      '@media (max-width: 768px)': {
                        margin: theme.spacing.sm,
                        marginBottom: 0,
                      },
                    })}
                  />
                  {detailView === 'subawardees' ? (
                    <ProjectSubawardeeCards
                      financials={selectedProjectFinancials}
                      labelById={labelById}
                      onOpenSubawardee={openSubawardee}
                    />
                  ) : (
                    <TcfMatchSection
                      componentProjectId={
                        componentProject ??
                        selectedProjectFinancials.componentProjectId
                      }
                      projectName={projectTitle(selectedProjectFinancials)}
                      financials={selectedProjectFinancials}
                      canEdit={canEditTcf}
                    />
                  )}
                </Stack>
              ) : (
                <ProjectSubawardeeCards
                  financials={selectedProjectFinancials}
                  labelById={labelById}
                  onOpenSubawardee={openSubawardee}
                />
              )}
            </>
          ) : null}

          {showAllProjects ? (
            <SimpleGrid cols={1} spacing="lg">
              {projectFinancials.map((financials) => (
                <ComponentProjectFinancialsCard
                  key={financials.componentProjectId}
                  financials={financials}
                  labelById={labelById}
                  onOpenSubawardee={openSubawardee}
                />
              ))}
            </SimpleGrid>
          ) : null}
        </Stack>
      ) : null}
    </Container>
  );
}
